/**
 * 🌐 Domain Router - Detecta el dominio y muestra la página correcta (estilo Shopify)
 *
 * - smarty.lat / www.smarty.lat → MarketingLandingPage (landing, público)
 * - web.smarty.lat → Panel de mercaderes (dashboard, POS, login)
 * - admin.smarty.lat → Solo super admin (gestión de tenants)
 * - dulti.smarty.lat → PublicHomePage del tenant (ecommerce público)
 * - localhost:8080 → En desarrollo = panel de mercaderes (web)
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { logDebug } from '@/lib/logger';
import { isMarketingDomain, isAdminDomain, isWebDomain, isDeprecatedLegacyDomain } from '@/lib/domain';
import MarketingLandingPage from '@/pages/MarketingLandingPage';
import DeprecatedUrlPage from '@/pages/DeprecatedUrlPage';
import { RedirectLoading } from '@/components/RedirectLoading';

function DomainRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [component, setComponent] = useState<'loading' | 'marketing' | 'redirect' | 'deprecated'>('loading');

  useEffect(() => {
    const checkDomain = async () => {
      const hostname = window.location.hostname;
      const hostnameWithoutPort = hostname.split(':')[0];

      logDebug('[DomainRouter] Detectando dominio', {
        hostname,
        hostnameWithoutPort,
        pathname: location.pathname,
      });

      // CASO 0: URLs antiguas dadas de baja (*.food.smarty.lat / *.food.smarty.local)
      if (isDeprecatedLegacyDomain(hostnameWithoutPort)) {
        logDebug('[DomainRouter] Detectado: URL antigua (dada de baja)', { hostnameWithoutPort });
        setComponent('deprecated');
        return;
      }

      // CASO 1: admin.smarty.lat → Solo super admin (redirigir a /admin)
      if (isAdminDomain(hostnameWithoutPort) || (hostnameWithoutPort === 'localhost' && location.pathname.startsWith('/admin'))) {
        logDebug('[DomainRouter] Detectado: Super Admin (admin.smarty)', { hostnameWithoutPort });
        if (location.pathname !== '/admin' && !location.pathname.startsWith('/admin/')) {
          navigate('/admin', { replace: true });
        }
        setComponent('redirect');
        return;
      }

      // CASO 2: web.smarty.lat o localhost → Panel de mercaderes (dashboard, POS, login)
      const isWeb = isWebDomain(hostnameWithoutPort);
      const isLocalhostApp =
        (hostnameWithoutPort === 'localhost' || hostnameWithoutPort === '127.0.0.1') &&
        !hostname.includes('.smarty.lat') &&
        !hostname.includes('.smarty.local');
      if (isWeb || isLocalhostApp) {
        logDebug('[DomainRouter] Detectado: Panel de mercaderes (web)', { hostnameWithoutPort });
        setComponent('redirect');
        if (location.pathname === '/' || location.pathname === '/inicio') {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            navigate('/home', { replace: true });
          } else {
            navigate('/login', { replace: true });
          }
        }
        return;
      }

      // CASO 3: smarty.lat / www.smarty.lat → Landing de marketing
      if (isMarketingDomain(hostnameWithoutPort)) {
        logDebug('[DomainRouter] Detectado: Landing de Marketing', { hostnameWithoutPort });
        setComponent('marketing');
        return;
      }

      // CASO 4 y 5 (subdominio de tenant / dominio personalizado → storefront)
      // DECOMISIONADOS del SPA: el storefront ahora lo sirve 100% el Cloudflare
      // Worker SSR. El SPA solo corre en admin/web/marketing, así que cualquier
      // host no reconocido cae a la landing de marketing.
      logDebug('[DomainRouter] Por defecto: Landing de Marketing');
      setComponent('marketing');
    };

    checkDomain();
  }, [location, navigate]);

  if (component === 'loading') {
    return <RedirectLoading />;
  }

  if (component === 'marketing') {
    return <MarketingLandingPage />;
  }

  if (component === 'deprecated') {
    return <DeprecatedUrlPage />;
  }

  // redirect: ya se llamó navigate(); mostrar carga para no dejar pantalla en blanco
  if (component === 'redirect') {
    return <RedirectLoading />;
  }

  return null;
}

export { DomainRouter };
export default DomainRouter;
